import { AdminBookingApiModel } from '@round/api';
import { BookingTableCellProps } from './helpers';
import { DropdownOptionWithSubLabel, OptionWithSubLabel, Select } from '@round/ui-kit';
import { useCallback, useEffect, useState } from 'react';
import { StylesConfig, ValueType } from 'react-select';
import { useInvoiceOptions } from '../../admin/Bookings/BookingsTableOptionsContext/useInvoiceOptions';
import { AdminBookingsTableRow } from '../../admin/Bookings/BookingsTable/AdminBookingsTable';

type InvoiceCellProps<T extends AdminBookingsTableRow> = BookingTableCellProps<T> & {
    updateBooking?: (bookingId: number, data: AdminBookingApiModel) => Promise<void>;
};

const selectStyles: StylesConfig = {
    control: (provided) => ({
        ...provided,
        height: '100%',
        border: 'none',
        boxShadow: 'none',
        backgroundColor: 'transparent',
    }),
    container: (provided) => ({
        ...provided,
        height: '100%',
        width: '10rem',
    }),
    valueContainer: (provided) => ({
        ...provided,
        height: '1.5rem',
        width: '100%',
    }),
    singleValue: (provided) => ({
        ...provided,
        height: '1.5rem',
        width: '85%',
        lineHeight: '1.5rem',
        fontSize: '0.75rem',
    }),
    input: (provided) => ({
        ...provided,
        height: '100%',
        fontSize: '0.75rem',
    }),
    menuList: (provided) => ({
        ...provided,
        overscrollBehavior: 'contain',
    }),
    menuPortal: (provided) => ({
        ...provided,
        overscrollBehavior: 'contain',
    }),
};

const InvoiceCell = <T extends AdminBookingsTableRow>({ updateBooking, row }: InvoiceCellProps<T>) => {
    const [search, setSearch] = useState('');
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isUpdateLoading, setIsUpdateLoading] = useState(false);
    const invoice = row.original.xero_invoice;

    const { invoiceOptions, onMenuScrollToBottom, isLoading } = useInvoiceOptions(search, isMenuOpen);

    const [selectedInvoiceOption, setSelectedInvoiceOption] = useState<
        ValueType<DropdownOptionWithSubLabel<number>, false>
    >(invoice ? { value: invoice.id, label: invoice.invoice_number, subLabel: invoice.reference } : null);

    const handleUpdateInvoice = useCallback(
        async (option: ValueType<DropdownOptionWithSubLabel<number>, false>) => {
            if (isUpdateLoading || option?.value === invoice?.id) {
                return;
            }
            setIsUpdateLoading(true);
            if (typeof updateBooking === 'function') {
                try {
                    await updateBooking(row.original.id, { client_invoice_id: option?.value ?? null });
                } catch {
                    setSelectedInvoiceOption(
                        invoice
                            ? { value: invoice.id, label: invoice.invoice_number, subLabel: invoice.reference }
                            : null
                    );
                } finally {
                    setIsUpdateLoading(false);
                }
            }
        },
        [isUpdateLoading, invoice, row.original.id, updateBooking]
    );

    useEffect(() => {
        if (invoice?.id !== selectedInvoiceOption?.value) {
            handleUpdateInvoice(selectedInvoiceOption);
        }
    }, [invoice, selectedInvoiceOption, handleUpdateInvoice]);

    return (
        <Select
            options={invoiceOptions}
            isDisabled={isUpdateLoading}
            value={selectedInvoiceOption}
            onChange={(option) => {
                setSelectedInvoiceOption(option);
            }}
            inputValue={search}
            onInputChange={setSearch}
            isLoading={isUpdateLoading || (isMenuOpen && isLoading)}
            onMenuScrollToBottom={onMenuScrollToBottom}
            styles={selectStyles}
            components={{ Option: OptionWithSubLabel }}
            menuPosition="fixed"
            onMenuClose={() => {
                setIsMenuOpen(false);
            }}
            onMenuOpen={() => {
                setIsMenuOpen(true);
            }}
            controlShouldRenderValue={!isMenuOpen}
            closeMenuOnScroll
            isClearable
            placeholder={isUpdateLoading ? 'Updating...' : isMenuOpen ? 'Search...' : 'Select invoice'}
        />
    );
};

export default InvoiceCell;
